import { DEFAULT_LANGUAGE } from './constants';

export const translations = {
  en: {
    optional: 'Optional',
    upload: 'Upload',
    uploading: 'Uploading...',
    select: 'Select...',
    selectDate: 'Select date',
    newsletterConfirmEmail:
      'If this address is new, we will send you a confirmation email. Your subscription starts once you confirm it.',

    validation: {
      required: 'This field is required',
      invalidEmail: 'Invalid email format',
      numericOnly: 'Only numeric characters are allowed',
      fileTooLarge: 'File {fileName} exceeds 10MB. Please upload a smaller file.',
      duplicateFile: 'Duplicate files are not allowed!',
      errorOccurred: 'An error occurred',
    },
  },
  fi: {
    optional: 'Valinnainen',
    upload: 'Lataa tiedosto',
    uploading: 'Ladataan...',
    select: 'Valitse...',
    selectDate: 'Valitse päivä',
    newsletterConfirmEmail: 'Jos osoite on uusi, lähetämme vahvistusviestin. Tilaus alkaa, kun vahvistat sen.',

    validation: {
      required: 'Tämä kenttä on pakollinen',
      invalidEmail: 'Virheellinen sähköpostiosoite',
      numericOnly: 'Vain numerot ovat sallittuja',
      fileTooLarge: 'Tiedosto {fileName} ylittää 10 Mt. Lataa pienempi tiedosto.',
      duplicateFile: 'Samaa tiedostoa ei voi lisätä kahdesti!',
      errorOccurred: 'Tapahtui virhe',
    },
  },
  sv: {
    optional: 'Valfritt',
    upload: 'Ladda upp',
    uploading: 'Laddar upp...',
    select: 'Välj...',
    selectDate: 'Välj datum',
    newsletterConfirmEmail:
      'Om adressen är ny skickar vi ett bekräftelsemejl. Prenumerationen börjar när du bekräftar den.',

    validation: {
      required: 'Detta fält är obligatoriskt',
      invalidEmail: 'Ogiltig e-postadress',
      numericOnly: 'Endast siffror är tillåtna',
      fileTooLarge: 'Filen {fileName} överskrider 10 MB. Ladda upp en mindre fil.',
      duplicateFile: 'Dubbletter av filer är inte tillåtna!',
      errorOccurred: 'Ett fel uppstod',
    },
  },
  de: {
    optional: 'Optional',
    upload: 'Hochladen',
    uploading: 'Wird hochgeladen...',
    select: 'Auswählen...',
    selectDate: 'Datum auswählen',
    newsletterConfirmEmail:
      'Wenn die Adresse neu ist, senden wir Ihnen eine Bestätigungs-E-Mail. Das Abonnement beginnt, sobald Sie es bestätigen.',

    validation: {
      required: 'Dieses Feld ist erforderlich',
      invalidEmail: 'Ungültige E-Mail-Adresse',
      numericOnly: 'Nur Zahlen sind erlaubt',
      fileTooLarge: 'Die Datei {fileName} überschreitet 10 MB. Bitte laden Sie eine kleinere Datei hoch.',
      duplicateFile: 'Doppelte Dateien sind nicht erlaubt!',
      errorOccurred: 'Ein Fehler ist aufgetreten',
    },
  },
};

/**
 * Returns the translation for a key in the given language.
 * Supports nested keys like 'validation.required' and falls back to English.
 *
 * @param {string} key - Translation key, optionally dot-separated.
 * @param {string} lang - Language code ('en', 'fi', 'sv', 'de').
 * @returns {string} Translated text or the key if translation not found.
 */
export const getTranslation = (key, lang = DEFAULT_LANGUAGE) => {
  const keys = key.split('.');

  const resolve = source =>
    keys.reduce(
      (value, part) => (value && typeof value === 'object' && part in value ? value[part] : undefined),
      source
    );

  return (
    resolve(translations[lang] || translations[DEFAULT_LANGUAGE]) ?? resolve(translations[DEFAULT_LANGUAGE]) ?? key
  );
};

// The admin persists whatever the optional-label key renders as in the operator's own UI language,
// so every translation of it is a platform default rather than customer-authored text.
const PLATFORM_OPTIONAL_LABELS = new Set(
  ['optional', ...Object.values(translations).map(({ optional }) => optional)].map(value => value.toLowerCase())
);

/**
 * Resolves the "(optional)" suffix: a customer-authored value wins, anything the platform seeded
 * falls through to the visitor's language. Idempotent, so it is safe to apply more than once.
 *
 * @param {string} storedLabel - optionalLabelText as stored on the field.
 * @param {string} lang - Active language code.
 * @returns {string} The suffix text to render.
 */
export const getOptionalLabel = (storedLabel, lang) => {
  const trimmed = storedLabel?.trim();

  return trimmed && !PLATFORM_OPTIONAL_LABELS.has(trimmed.toLowerCase()) ? trimmed : getTranslation('optional', lang);
};
